import React, { useEffect, useState, useMemo } from 'react';
import Select from 'react-select';
// Common Components (Corrected Paths)
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppToaster from '../../AppToaster';
// API & Utils
import { createVoucher, fetchVoucher, updateVoucher } from '../../../api/VouchersAPI';
import { getVoucherTypes } from '../../../api/VouchersAPI';
import { getParties } from '../../../api/PartiesAPI';
import { getHoas } from '../../../api/HoasAPI';
import { handleApiError } from '../../../api/risapi';
// Hooks & Helpers
import { useNavigate } from 'react-router-dom';
import { capitalizeFirstLetter } from '../../../commonfunctions';
// Date Picker
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from 'date-fns';

const initialDetailLine = { id: null, hoa_id: null, debit_amount: '', credit_amount: '' };
const initialVoucherState = {
    voucher_number: '',
    voucher_type_id: null,
    voucher_date: new Date(),
    party_id: null,
    narration: '',
    details: [
        { ...initialDetailLine },
        { ...initialDetailLine }
    ]
};

const VoucherForm = ({ formType, currentVoucherId }) => {
    const navigate = useNavigate();
    const [voucher, setVoucher] = useState(initialVoucherState);
    const [errors, setErrors] = useState({});
    const isCreating = formType === 'create';
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);
    const [loadingVoucherData, setLoadingVoucherData] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isFormDisabled, setIsFormDisabled] = useState(false);


    // Dropdown Options
    const [voucherTypeOptions, setVoucherTypeOptions] = useState([]);
    const [partyOptions, setPartyOptions] = useState([]);

    // ** FIX: Create two state lists for HOAs **
    const [allHoaOptions, setAllHoaOptions] = useState([]);
    const [manualHoaOptions, setManualHoaOptions] = useState([]);


    // --- Fetch Dropdown Data ---
    useEffect(() => {
        let isMounted = true;
        setLoadingDropdowns(true);
        const fetchDropdowns = async () => {
            try {
                const [partyRes, hoaRes, vchTypeRes] = await Promise.all([
                    new Promise((resolve) => getParties({ paginate: false }, resolve)),
                    new Promise((resolve) => getHoas({ paginate: false }, resolve)),
                    new Promise((resolve) => getVoucherTypes({ paginate: false }, resolve)),
                ]);
                if (!isMounted) return;

                if (partyRes.parties?.data || Array.isArray(partyRes.parties)) {
                    setPartyOptions((Array.isArray(partyRes.parties) ? partyRes.parties : partyRes.parties.data).map(p => ({ value: p.id, label: `${p.name} ${p.code ? `(${p.code})` : ''}` })));
                } else { throw new Error('Invalid party data'); }

                if (hoaRes.hoas) {
                    const hoasData = Array.isArray(hoaRes.hoas) ? hoaRes.hoas : (hoaRes.hoas.data || []);

                    // ** FIX: Populate both HOA lists **
                    // 1. All HOAs (for viewing system vouchers)
                    const allHoas = hoasData.map(h => ({ value: h.id, label: `${h.name} (${h.code})` }));

                    // 2. Manual HOAs (for creating/editing manual vouchers)
                    const manualHoas = hoasData.filter(h => !h.is_system_defined && !h.is_gst_head)
                        .map(h => ({ value: h.id, label: `${h.name} (${h.code})` }));

                    setAllHoaOptions(allHoas);
                    setManualHoaOptions(manualHoas);

                } else { throw new Error('Invalid HOA data'); }

                if (vchTypeRes.voucher_types) {
                    setVoucherTypeOptions((Array.isArray(vchTypeRes.voucher_types) ? vchTypeRes.voucher_types : []).map(vt => ({ value: vt.id, label: vt.name })));
                } else { throw new Error('Invalid Voucher Type data'); }

            } catch (error) {
                if (!isMounted) return;
                console.error("Error fetching dropdown data:", error);
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not load required master data.' });
                setShowToaster(true);
            } finally {
                if (isMounted) setLoadingDropdowns(false);
            }
        };
        fetchDropdowns();
        return () => { isMounted = false; };
    }, []);

    // --- Fetch Voucher Data in Edit Mode ---
    useEffect(() => {
        if (loadingDropdowns || formType === 'create' || !currentVoucherId) {
            if (formType === 'create') { setLoadingVoucherData(false); setIsFormDisabled(false); }
            return;
        }
        let isMounted = true;
        setVoucher(initialVoucherState);
        setErrors({}); setLoadingVoucherData(true);
        setIsFormDisabled(true);

        fetchVoucher(currentVoucherId, (response) => {
            if (!isMounted) return;
            setLoadingVoucherData(false);
            if (response.voucher) {
                const fetched = response.voucher;

                const isLocked = (fetched.voucher_details || []).some(d => d.referenceable_type);
                setIsFormDisabled(isLocked);
                if (isLocked) {
                    setToaster({ type: 'info', heading: 'Read Only', message: 'This is a system-generated voucher and cannot be edited here.' });
                    setShowToaster(true);
                }

                let vchDate = new Date();
                if (fetched.voucher_date) { try { vchDate = parseISO(fetched.voucher_date + 'T00:00:00'); if (isNaN(vchDate)) vchDate = new Date(); } catch (e) { vchDate = new Date(); } }

                const formattedDetails = (Array.isArray(fetched.voucher_details) ? fetched.voucher_details : []).map(detail => ({
                    id: detail.id,
                    hoa_id: detail.hoa_id,
                    debit_amount: parseFloat(detail.debit_amount || 0) > 0 ? detail.debit_amount : '',
                    credit_amount: parseFloat(detail.credit_amount || 0) > 0 ? detail.credit_amount : '',
                }));

                setVoucher({
                    voucher_number: fetched.voucher_number || '',
                    voucher_type_id: fetched.voucher_type_id,
                    voucher_date: vchDate,
                    party_id: fetched.party_id,
                    narration: fetched.narration || '',
                    details: formattedDetails.length > 0 ? formattedDetails : initialVoucherState.details
                });

            } else {
                handleApiError(response, setErrors);
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not load Voucher data.' });
                setShowToaster(true);
            }
        });
        return () => { isMounted = false; };
    }, [formType, currentVoucherId, loadingDropdowns, navigate]);


    // --- Input Handlers (Header) ---
    const handleInputChange = (e) => {
        if (isFormDisabled) return;
        const { name, value } = e.target;
        setVoucher(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };
    const handleDateChange = (date) => {
        if (!isFormDisabled) setVoucher(prev => ({ ...prev, voucher_date: date }));
        if (errors.voucher_date) setErrors(prev => ({ ...prev, voucher_date: null }));
    };
    const handleSelectChange = (fieldName, selectedOption) => {
        if (isFormDisabled) return;
        setVoucher(prev => ({ ...prev, [fieldName]: selectedOption ? selectedOption.value : null }));
        if (errors[fieldName]) setErrors(prev => ({ ...prev, [fieldName]: null }));
    };

    // --- Detail Line Handlers ---
    const addDetailLine = () => {
        if (isFormDisabled) return;
        setVoucher(prev => ({ ...prev, details: [...(prev.details || []), { ...initialDetailLine }] }));
    };
    const removeDetailLine = (index) => {
        if (isFormDisabled) return;
        setVoucher(prev => {
            const newDetails = (prev.details || []).filter((_, i) => i !== index);
            if (newDetails.length < 2) {
                newDetails.push({ ...initialDetailLine });
                if (newDetails.length < 2) newDetails.push({ ...initialDetailLine });
            }
            return { ...prev, details: newDetails };
        });
    };
    const handleDetailSelectChange = (index, fieldName, selectedOption) => {
        if (isFormDisabled) return;
        setVoucher(prev => {
            const newDetails = [...(prev.details || [])];
            if (newDetails[index]) newDetails[index][fieldName] = selectedOption ? selectedOption.value : null;
            return { ...prev, details: newDetails };
        });
    };
    const handleDetailAmountChange = (index, fieldName, value) => {
        if (isFormDisabled) return;
        setVoucher(prev => {
            const newDetails = [...(prev.details || [])];
            if (!newDetails[index]) return prev;

            const numValue = value === '' ? '' : parseFloat(value);
            if (numValue < 0) return prev;

            newDetails[index][fieldName] = numValue;

            if (numValue > 0) {
                if (fieldName === 'debit_amount') newDetails[index]['credit_amount'] = '';
                if (fieldName === 'credit_amount') newDetails[index]['debit_amount'] = '';
            }

            return { ...prev, details: newDetails };
        });
    };

    // --- Totals Calculation ---
    const totals = useMemo(() => {
        let debit = 0;
        let credit = 0;
        (voucher.details || []).forEach(d => {
            debit += parseFloat(d.debit_amount || 0);
            credit += parseFloat(d.credit_amount || 0);
        });
        const isBalanced = Math.abs(debit - credit) < 0.001;
        const total = debit;
        return { debit, credit, total, isBalanced };
    }, [voucher.details]);

    // --- Save Handler ---
    const saveForm = (exitForm = false) => {
        // --- 1. Form Validation (Keep your existing validation logic here) ---
        if (!totals.isBalanced) {
            setToaster({ type: 'danger', heading: 'Validation Error', message: 'Debit and Credit totals must match.' });
            setShowToaster(true);
            return;
        }

        // --- 2. Prepare Data ---
        const dataToSend = {
            ...voucher,
            // Format date for Laravel
            voucher_date: voucher.voucher_date instanceof Date && !isNaN(voucher.voucher_date)
                ? format(voucher.voucher_date, 'yyyy-MM-dd')
                : null,
            // Filter out empty lines
            details: voucher.details.filter(line => line.hoa_id)
        };

        setSaving(true);
        setErrors({}); // Clear previous errors

        // --- 3. Centralized API Response Handler (Fixes the hanging button) ---
        const handleResponse = (response) => {
            setSaving(false);

            // Success Path
            if (response.voucher) {
                const message = `Voucher #${response.voucher.voucher_number || response.voucher.id} ${isCreating ? 'created' : 'updated'} successfully.`;
                if (exitForm) {
                    navigate('/franchisee_user/finance/vouchers', { state: { message } });
                } else {
                    setVoucher(initialVoucherState); // Reset form for next entry
                    setErrors({});
                    setToaster({ type: 'success', heading: 'Success', message });
                    setShowToaster(true);
                }
            }
            // Error Path
            else {
                // Target the nested Axios error object correctly
                const apiError = response.error || response;

                // Explicitly capture 422 Validation Errors so they appear under inputs
                if (apiError?.response?.status === 422 && apiError?.response?.data?.errors) {
                    setErrors(apiError.response.data.errors);
                } else {
                    handleApiError(apiError, setErrors);
                }

                setToaster({
                    type: 'danger',
                    heading: 'Error',
                    message: apiError?.response?.data?.message || 'Could not save voucher. Please check fields.'
                });
                setShowToaster(true);
            }
        };

        // --- 4. Parameter Mismatch Fix ---
        // Explicitly calling the correct API with the exact parameters it expects
        if (isCreating) {
            createVoucher(dataToSend, handleResponse);
        } else {
            updateVoucher(currentVoucherId, dataToSend, handleResponse);
        }
    };

    // --- Reset Handler ---
    const handleReset = () => {
        if (isFormDisabled || saving) return;
        if (formType === 'create') {
            setVoucher(initialVoucherState);
            setErrors({});
        } else if (currentVoucherId) {
            setLoadingDropdowns(true);
            setTimeout(() => setLoadingDropdowns(false), 0);
        }
    };

    const getTitle = () => formType === 'create' ? 'Create Voucher' : (isFormDisabled ? 'View Voucher' : 'Edit Voucher');
    const isLoading = loadingDropdowns || loadingVoucherData;

    // ** FIX: Dynamically choose the correct list of HOAs **
    const currentHoaOptions = isFormDisabled ? allHoaOptions : manualHoaOptions;

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main><div className="container-fluid">
                <AppBreadCrumb title={getTitle()}>
                    <li><a className="f-s-14 f-w-500" href="#">Finance</a></li>
                    <li><a className="f-s-14 f-w-500" href="/franchisee_user/finance/vouchers">Vouchers</a></li>
                    <li className="active"><a className="f-s-14 f-w-500" href="#">{capitalizeFirstLetter(formType)}</a></li>
                </AppBreadCrumb>

                {isLoading && <div className="loader_box"><div className="loader_14"></div></div>}

                {!isLoading && (
                    <div className="row"><div className="col-12"><div className="card">
                        <div className="card-header"><h5>{getTitle()} {formType === 'edit' ? `#${voucher.voucher_number || currentVoucherId}` : ''}</h5></div>
                        <div className="card-body">
                            <form className="app-form" noValidate onSubmit={(e) => e.preventDefault()}>
                                {/* --- Header Section --- */}
                                <div className="row border-bottom pb-3 mb-3">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Voucher Type *</label>
                                        <Select options={voucherTypeOptions} value={voucherTypeOptions.find(o => o.value === voucher.voucher_type_id)} onChange={(opt) => handleSelectChange('voucher_type_id', opt)} placeholder="Select Type" isClearable isDisabled={isFormDisabled || loadingDropdowns} />
                                        <span className="text-danger">{errors?.voucher_type_id}</span>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Date *</label>
                                        <DatePicker selected={voucher.voucher_date} onChange={handleDateChange} dateFormat="yyyy-MM-dd" className="form-control" wrapperClassName="d-block" disabled={isFormDisabled} />
                                        <span className="text-danger">{errors?.voucher_date}</span>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Party (Optional)</label>
                                        <Select options={partyOptions} value={partyOptions.find(o => o.value === voucher.party_id)} onChange={(opt) => handleSelectChange('party_id', opt)} placeholder="Select Party (if any)" isClearable isDisabled={isFormDisabled || loadingDropdowns} />
                                        <span className="text-danger">{errors?.party_id}</span>
                                    </div>
                                    <div className="col-sm-12 col-md-12 mb-3">
                                        <label className="form-label">Narration <span className="text-danger">*</span></label>
                                        <textarea
                                            className={`form-control ${errors.narration ? 'is-invalid' : ''}`}
                                            rows="2"
                                            value={voucher.narration}
                                            onChange={(e) => setVoucher({ ...voucher, narration: e.target.value })}
                                            disabled={isFormDisabled}
                                        />
                                        {/* Correctly extracting the first string from Laravel's error array */}
                                        <span className="text-danger">
                                            {errors?.narration && (Array.isArray(errors.narration) ? errors.narration[0] : errors.narration)}
                                        </span>
                                    </div>
                                </div>

                                {/* --- Details Section --- */}
                                <h6 className="mb-3">Voucher Details</h6>
                                <div className="table-responsive">
                                    <table className="table table-bordered table-sm">
                                        <thead className='table-light'>
                                            <tr>
                                                <th style={{ width: '50%' }}>Account Head (HOA) *</th>
                                                <th style={{ width: '20%' }} className='text-end'>Debit *</th>
                                                <th style={{ width: '20%' }} className='text-end'>Credit *</th>
                                                <th style={{ width: '10%' }} className='text-center'>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(voucher.details) && voucher.details.map((detail, index) => (
                                                <tr key={detail.id || `detail-${index}`}>
                                                    <td>
                                                        <Select
                                                            // ** FIX: Use the dynamic options list **
                                                            options={currentHoaOptions}
                                                            value={currentHoaOptions.find(o => o.value === detail.hoa_id)}
                                                            onChange={(sel) => handleDetailSelectChange(index, 'hoa_id', sel)}
                                                            isDisabled={isFormDisabled}
                                                            placeholder="Select Account"
                                                            menuPortalTarget={document.body}
                                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                        />
                                                        <span className="text-danger small">{errors?.details?.[index]?.hoa_id}</span>
                                                    </td>
                                                    <td>
                                                        <input type="number" step="0.01" min="0" className="form-control form-control-sm text-end"
                                                            value={detail.debit_amount || ''}
                                                            onChange={(e) => handleDetailAmountChange(index, 'debit_amount', e.target.value)}
                                                            disabled={isFormDisabled || (detail.credit_amount !== '' && detail.credit_amount > 0)}
                                                        />
                                                        <span className="text-danger small">{errors?.details?.[index]?.debit_amount}</span>
                                                    </td>
                                                    <td>
                                                        <input type="number" step="0.01" min="0" className="form-control form-control-sm text-end"
                                                            value={detail.credit_amount || ''}
                                                            onChange={(e) => handleDetailAmountChange(index, 'credit_amount', e.target.value)}
                                                            disabled={isFormDisabled || (detail.debit_amount !== '' && detail.debit_amount > 0)}
                                                        />
                                                        <span className="text-danger small">{errors?.details?.[index]?.credit_amount}</span>
                                                    </td>
                                                    <td className='text-center align-middle'>
                                                        {!isFormDisabled && (
                                                            <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeDetailLine(index)}>
                                                                <i className="ti ti-trash f-s-14"></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td className='text-end fw-bold'>Totals</td>
                                                <td className={`text-end fw-bold ${!totals.isBalanced ? 'text-danger' : ''}`}>
                                                    {totals.debit.toFixed(2)}
                                                </td>
                                                <td className={`text-end fw-bold ${!totals.isBalanced ? 'text-danger' : ''}`}>
                                                    {totals.credit.toFixed(2)}
                                                </td>
                                                <td></td>
                                            </tr>
                                            {!isFormDisabled && (
                                                <tr>
                                                    <td colSpan="4">
                                                        <button type="button" className="btn btn-sm btn-outline-success" onClick={addDetailLine}>+ Add Line</button>

                                                    </td>
                                                </tr>
                                            )}
                                        </tfoot>
                                    </table>
                                </div>
                                <span className="text-danger small">{errors?.details && typeof errors.details === 'string' ? errors.details : ''}</span>

                                {!totals.isBalanced && !isFormDisabled && (
                                    <div className="alert alert-danger mt-2" role="alert">
                                        Voucher is unbalanced! Debits and Credits must be equal.
                                    </div>
                                )}

                                {/* --- Action Buttons --- */}
                                <div className="col-12 mt-4">
                                    <div className="text-end border-top pt-3">
                                        {!isFormDisabled ? (
                                            <>
                                                <button className="btn btn-primary me-2 btn-sm" type="button" onClick={() => saveForm(true)} disabled={saving || isLoading || !totals.isBalanced || totals.total === 0}>
                                                    {saving ? 'Saving...' : (formType === 'create' ? 'Save Voucher' : 'Update Voucher')}
                                                </button>
                                                <button className="btn btn-danger btn-sm" onClick={handleReset} type="button" disabled={saving || isLoading}>Reset</button>
                                                <button className="btn btn-light-reddit btn-sm" type="button" onClick={() => navigate('/franchisee_user/finance/vouchers')} disabled={saving}>Go Back</button>
                                            </>
                                        ) : (
                                            <button className="btn btn-light-reddit btn-sm" type="button" onClick={() => navigate('/franchisee_user/finance/vouchers')} disabled={saving}>Close View</button>
                                        )}

                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    </div>
                    </div>
                )}
            </div>
            </main>
        </>
    );
};

export default VoucherForm;